import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux';
import { reviewAssestmentSubmission } from '../../../../../../Constants/Api/functions/function';
import { getGradeDetails, GRADING_RUBRIC_SCALE } from '../../../../../../Utils/gradeUtils';

// Antd
import { Button, Divider, Drawer, Form, Input, notification, Spin, Tag, Space, Descriptions } from 'antd';
import {
    ExportOutlined,
    TrophyOutlined,
    EditOutlined,
    UserOutlined
} from '@ant-design/icons';

// Quill
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

import './style.scss';

const modules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ align: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

const UpdateReviewSubmission = ({
    isOpen,
    setOpen,
    handleGetSubmissions,
    submissionData,
    userData,
}) => {
    const [api, contextHolder] = notification.useNotification();
    const [isLoading, setIsLoading] = useState(false);
    const [marksVal, setMarksVal] = useState('');
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const formRef = useRef();

    useEffect(() => {
        if (isOpen && submissionData) {
            setMarksVal(submissionData?.marks !== undefined && submissionData?.marks !== null ? String(submissionData.marks) : '');
            if (formRef.current) {
                formRef.current.setFieldsValue({
                    marks: submissionData.marks,
                    comments: submissionData.comments || '',
                });
            }
        }
    }, [isOpen, submissionData]);

    const onFinish = async (values) => {
        Object.keys(values).forEach(key => (values[key] === undefined || values[key] === '') && delete values[key]);
        values.id = submissionData?.id;
        values.submissionId = submissionData?.id;
        values.reviewdBy = userProfile?.id;

        setIsLoading(true);

        try {
            const res = await reviewAssestmentSubmission(values);
            if (res && (res.status === 200 || res.success)) {
                openNotification(
                    'success',
                    'Review Updated',
                    'The submission review and marks have been updated successfully.'
                );
                setOpen(false);
                await handleGetSubmissions();
            } else {
                openNotification(
                    'error',
                    'Error',
                    res?.data?.message || 'Failed to update review'
                );
            }
        } catch (error) {
            console.error('UpdateReviewSubmission', error);
            openNotification('error', 'Error', 'Something went wrong while updating review');
        }

        setIsLoading(false);
    };

    const openNotification = (type, message, description) => {
        api[type]({
            message: message,
            description: description,
        });
    };

    const currentGrade = getGradeDetails(marksVal);

    return (
        <div>
            {contextHolder}
            <Drawer
                title={
                    <Space size={8}>
                        <EditOutlined style={{ color: '#1677ff' }} />
                        <span>Update Submission Evaluation & Grade</span>
                    </Space>
                }
                width={typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : 720}
                onClose={() => setOpen(false)}
                open={isOpen}
                className='submission-review-drawer'
            >
                {/* Student & Submission Info Card */}
                <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '16px 20px',
                    marginBottom: 20
                }}>
                    <Descriptions
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 10 }}>
                                <Space size={8}>
                                    <UserOutlined style={{ color: '#2563eb' }} />
                                    <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                                        {userData?.name || submissionData?.name || 'Student Submission'}
                                    </span>
                                </Space>
                                {submissionData?.submissionUrl && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<ExportOutlined />}
                                        href={submissionData.submissionUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Submitted Work
                                    </Button>
                                )}
                            </div>
                        }
                        column={{ xs: 1, sm: 2 }}
                        size="small"
                    >
                        <Descriptions.Item label="Email">
                            {userData?.email || submissionData?.email || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Attempt #">
                            <Tag color="cyan">Attempt {submissionData?.attemptNumber || 1}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Submitted At">
                            {submissionData?.createdAt ? new Date(submissionData.createdAt).toLocaleString() : 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color="success">Reviewed / Graded</Tag>
                        </Descriptions.Item>
                    </Descriptions>
                </div>

                <Divider style={{ margin: '14px 0 20px' }} />

                <Spin spinning={isLoading} tip="Updating evaluation...">
                    <Form
                        layout="vertical"
                        onFinish={onFinish}
                        ref={formRef}
                    >
                        {/* Marks & Real-time Grade Rubric */}
                        <div style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: 10,
                            padding: '16px 18px',
                            marginBottom: 20
                        }}>
                            <Form.Item
                                label={<strong style={{ fontSize: 14 }}>Award Marks (0 - 100%)</strong>}
                                name="marks"
                                rules={[
                                    { required: true, message: 'Please enter marks!' },
                                    {
                                        validator: (_, value) => {
                                            if (value === undefined || value === '') return Promise.resolve();
                                            const num = Number(value);
                                            if (isNaN(num) || num < 0 || num > 100) {
                                                return Promise.reject('Marks must be between 0 and 100');
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                style={{ marginBottom: 12 }}
                            >
                                <Input
                                    type='number'
                                    placeholder="Enter marks from 0 to 100"
                                    min={0}
                                    max={100}
                                    size="large"
                                    value={marksVal}
                                    onChange={(e) => setMarksVal(e.target.value)}
                                    suffix="%"
                                    style={{ maxWidth: 220, fontSize: 16 }}
                                />
                            </Form.Item>

                            {/* Dynamic Grade & Classification Feedback */}
                            {marksVal !== '' && !isNaN(Number(marksVal)) && Number(marksVal) >= 0 && Number(marksVal) <= 100 ? (
                                <div style={{
                                    marginTop: 6,
                                    padding: '10px 14px',
                                    background: '#f8fafc',
                                    border: `1.5px solid ${currentGrade.color}`,
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 8
                                }}>
                                    <Space size={8}>
                                        <TrophyOutlined style={{ color: currentGrade.color, fontSize: 18 }} />
                                        <div>
                                            <span style={{ fontSize: 12, color: '#64748b' }}>Assigned Grade: </span>
                                            <strong style={{ fontSize: 16, color: currentGrade.color }}>
                                                Grade {currentGrade.grade} — {currentGrade.classification}
                                            </strong>
                                        </div>
                                    </Space>
                                    <Tag color={currentGrade.tagColor} style={{ fontSize: 13, padding: '3px 12px', fontWeight: 600 }}>
                                        {currentGrade.classification}
                                    </Tag>
                                </div>
                            ) : null}

                            {/* Standard Rubric Reference */}
                            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                                    Standard Grading Scale:
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {GRADING_RUBRIC_SCALE.map((rubric) => (
                                        <Tag
                                            key={rubric.grade}
                                            color={rubric.tagColor}
                                            style={{
                                                fontSize: 11,
                                                padding: '2px 8px',
                                                border: marksVal !== '' && currentGrade.grade === rubric.grade ? '2px solid #0f172a' : undefined
                                            }}
                                        >
                                            <strong>{rubric.grade}</strong> ({rubric.min}-{rubric.max}%): {rubric.classification}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Comments & Feedback */}
                        <Form.Item
                            label={<strong style={{ fontSize: 14 }}>Evaluator Feedback & Comments</strong>}
                            name="comments"
                        >
                            <ReactQuill
                                modules={modules}
                                theme="snow"
                                placeholder="Update your constructive feedback for the student..."
                                style={{
                                    height: 200,
                                    marginBottom: 50,
                                }}
                            />
                        </Form.Item>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                            <Button onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" size="large" icon={<EditOutlined />}>
                                Update Evaluation & Grade
                            </Button>
                        </div>
                    </Form>
                </Spin>
            </Drawer>
        </div>
    );
};

export default UpdateReviewSubmission;